// src/hooks/useUserPermissions.ts
import { useMemo } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useAppStore } from '../store/appStore';
import { AppLogger } from '../utils/loggers';
import type { UserPermissions } from '../config/navigation.config';

// Mock Keycloak token parsing - replace with actual Keycloak integration
interface KeycloakToken {
  sub: string;
  realm_access?: {
    roles: string[];
  };
  resource_access?: {
    [client: string]: {
      roles: string[];
    };
  };
  groups?: string[];
  preferred_username?: string;
  email?: string;
  name?: string;
}

// Parse roles from Keycloak token
const parseKeycloakRoles = (token: KeycloakToken | null): string[] => {
  AppLogger.permissions.parseRoles({ token });
  
  if (!token) {
    AppLogger.permissions.parseRoles({ noToken: true });
    return [];
  }
  
  AppLogger.permissions.tokenStructure({ token });
  
  const realmRoles = token.realm_access?.roles || [];
  const clientRoles: string[] = [];
  
  AppLogger.permissions.realmRoles(realmRoles, { realmAccess: token.realm_access });
  AppLogger.permissions.realmAccess({ realmAccess: token.realm_access });
  
  // Extract client-specific roles
  if (token.resource_access) {
    AppLogger.permissions.resourceAccess({ resourceAccess: token.resource_access });
    Object.values(token.resource_access).forEach(client => {
      AppLogger.permissions.clientRoles(client.roles);
      clientRoles.push(...client.roles);
    });
  }
  
  const allRoles = [...realmRoles, ...clientRoles];
  const groups = token.groups || [];
  
  AppLogger.permissions.allRoles(allRoles);
  AppLogger.permissions.groups(groups);
  
  // Check if user is in admin groups (alternative to role-based access)
  const isInAdminGroup = groups.some(group => 
    ['Administrators', '/Administrators'].includes(group)
  );
  
  // If user is in admin group, ensure they have admin role access
  if (isInAdminGroup && !allRoles.includes('admin')) {
    AppLogger.permissions.userRoles({ adminGroupDetected: true, addingAdminRole: true });
    allRoles.push('admin');
  }
  
  AppLogger.permissions.userRoles({
    realmRoles,
    clientRoles,
    groups,
    finalRoles: allRoles,
    hasAdminRole: allRoles.includes('admin'),
    isInAdminGroup,
    adminGroupDetection: {
      groupsFound: groups,
      isInAdminGroup,
      adminRoleAdded: isInAdminGroup && !realmRoles.includes('admin')
    }
  });
  
  return allRoles;
};

// Check if user has admin privileges
const isAdminRole = (roles: string[]): boolean => {
  return roles.some(role => 
    ['admin', 'realm-admin', 'system-admin', 'super-admin'].includes(role.toLowerCase())
  );
};

// Check if user has system-wide admin privileges
const isSystemAdminRole = (roles: string[]): boolean => {
  return roles.some(role => 
    ['system-admin', 'super-admin'].includes(role.toLowerCase())
  );
};

/**
 * Custom hook to get user permissions from Keycloak token and app state
 */
export const useUserPermissions = (): UserPermissions & {
  isLoading: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
} => {
  const { currentFirmId, currentUserId, isAuthenticated } = useAppStore();
  const { keycloak } = useKeycloak();
  
  AppLogger.permissions.check({
    currentFirmId,
    currentUserId,
    isAuthenticated,
    keycloakAuthenticated: keycloak.authenticated,
    keycloakTokenParsed: keycloak.tokenParsed,
    keycloakTokenParsedType: typeof keycloak.tokenParsed
  });
  
  // Use actual Keycloak token instead of mock data
  const keycloakToken: KeycloakToken | null = useMemo(() => {
    AppLogger.permissions.tokenMemo({
      keycloakAuthenticated: keycloak.authenticated,
      tokenParsed: keycloak.tokenParsed,
      isAuthenticated,
      currentUserId
    });
    
    // Simplified: Just use Keycloak's own authentication state
    if (!keycloak.authenticated || !keycloak.tokenParsed) {
      AppLogger.permissions.tokenMemo({
        returningNull: true,
        keycloakAuthenticated: keycloak.authenticated,
        hasTokenParsed: !!keycloak.tokenParsed
      });
      return null;
    }
    
    AppLogger.permissions.tokenReturn({ token: keycloak.tokenParsed });
    // Return the actual parsed Keycloak token
    return keycloak.tokenParsed as KeycloakToken;
  }, [keycloak.authenticated, keycloak.tokenParsed]);
  
  const permissions = useMemo((): UserPermissions => {
    AppLogger.permissions.permissionsMemo({ keycloakToken });
    const roles = parseKeycloakRoles(keycloakToken);
    
    const result = {
      roles,
      realmId: currentFirmId || '',
      isAdmin: isAdminRole(roles),
      isSystemAdmin: isSystemAdminRole(roles),
    };
    
    AppLogger.permissions.permissionsReturn(result);
    return result;
  }, [keycloakToken, currentFirmId]);
  
  const hasRole = (role: string): boolean => {
    return permissions.roles.includes(role);
  };
  
  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some(role => permissions.roles.includes(role));
  };
  
  const hasAllRoles = (roles: string[]): boolean => {
    return roles.every(role => permissions.roles.includes(role));
  };
  
  return {
    ...permissions,
    isLoading: !keycloak.authenticated,
    hasRole,
    hasAnyRole,
    hasAllRoles,
  };
};

/**
 * Hook to check if user has permission for a specific route
 */
export const useRoutePermission = (requiredRoles?: string[]): boolean => {
  const { hasAnyRole } = useUserPermissions();
  
  if (!requiredRoles || requiredRoles.length === 0) return true;
  return hasAnyRole(requiredRoles);
};

/**
 * Hook to get user display information
 */
export const useUserInfo = () => {
  const { currentUserId, isAuthenticated } = useAppStore();
  
  // Import Keycloak hook to get real user data
  const { keycloak } = useKeycloak();
  
  const userInfo = useMemo(() => {
    if (!isAuthenticated || !currentUserId || !keycloak.tokenParsed) return null;
    
    const token = keycloak.tokenParsed;
    
    // Extract user information from Keycloak token
    const firstName = token.given_name || token.name?.split(' ')[0] || '';
    const lastName = token.family_name || token.name?.split(' ').slice(1).join(' ') || '';
    const fullName = token.name || `${firstName} ${lastName}`.trim() || token.preferred_username || 'User';
    const email = token.email || '';
    
    // Generate initials from the name
    const initials = fullName
      .split(' ')
      .map((part: string) => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
    
    return {
      id: currentUserId,
      username: token.preferred_username || '',
      email,
      firstName,
      lastName,
      fullName,
      initials,
      avatar: null,
    };
  }, [isAuthenticated, currentUserId, keycloak.tokenParsed]);
  
  return userInfo;
};
